import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import CharityContextProvider from "../../../context/charity/provider";
import CharityContext from "../../../context/charity/context";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import { Button, Col, Divider, Row, Select } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import { getTranslations } from "../../../utils/helpers/translations";
import { IUpdateCharity } from "../../../models/charity/request";
import { execute } from "../../../utils/helpers/execute";
import styles from "../../trademark/trademark-form/style.module.scss";
import Spin from "../../general/antd/spin";
import { languages } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import FormItem from "../../general/form-item";
import Controller from "../../form-components/controller";
import ImageUploader from "../../form-components/image-uploader";
import { MediumFor } from "../../../models/medium/enum";
import OldImage from "../../form-components/old-image";
import EndPoints from "../../../services/end-points";
import { IBadgeCharity } from "../../../models/badge_charity/response";
import { ICity } from "../../../models/city/response";
import { ICategoryCharity } from "../../../models/category_charity/response";
import { getUserStatus } from "../../../models/user/enum";

const Form = () => {
    const { t } = useTranslation();
    const { details, actions, loading } = useContext(CharityContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const { direction } = useContext(AppContext);
    const [tags, setTags] = useState<IBadgeCharity[]>([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [cities, setCities] = useState<ICity[]>([]);
    const [citiesLoading, setCitiesLoading] = useState(true);
    const [cats, setCats] = useState<ICategoryCharity[]>([]);
    const [catsLoading, setCatsLoading] = useState(true);
    const Option = Select.Option;
    const methods = useForm<any>({
        defaultValues: {
            name: getTranslations(details?.translations?.name),
            description: getTranslations(details?.translations?.description),
            content: getTranslations(details?.translations?.content),
            servicesProvided: getTranslations(details?.translations?.servicesProvided),
            hotLine: details?.hot_line,
            status: details?.status,
            address: details?.address,
            mobile: details?.mobile,
            nameManager: details?.name_manager,
            phoneManager: details?.phone_manager,
            phone: details?.phone,
            serial_number: details?.serial_number,
            telegram: details?.telegram,
            whatsApp: details?.whats_app,
            facebook: details?.facebook,
            instagram: details?.instagram,
            establishmentDate: details?.establishment_date,
            webSiteLink: details?.web_site_link,
            imageId: details?.image?.id,
            coverId: details?.cover?.id,
            identificationDocId: details?.identification_doc?.id,
            numberOfBeneficiaries: details?.number_of_beneficiaries || 0,
            categoryCharityId: details?.categoryCharity?.id,
            badgeId: details?.badge?.id,
            cityId: details?.city?.id,
        } as IUpdateCharity,
    });

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        formState: { errors },
    } = methods;

    useEffect(() => {
        const getCats = async () => {
            try {
                const { data } = await EndPoints.categoryCharity.getAllCategoriesCharities({
                    light: true,
                    perPage: -1,
                });
                setCats(data.data);
            } catch (error) {
            } finally {
                setCatsLoading(false);
            }
        };
        getCats();
    }, [direction]);

    useEffect(() => {
        const getTags = async () => {
            try {
                const { data } = await EndPoints.badgeCharity.getAllBadgeCharities({
                    light: true,
                    perPage: -1,
                });
                setTags(data.data);
            } catch (error) {
            } finally {
                setTagsLoading(false);
            }
        };
        getTags();
    }, [direction]);

    useEffect(() => {
        const getCities = async () => {
            try {
                const { data } = await EndPoints.city.getAllCities({
                    light: true,
                    perPage: -1,
                });
                setCities(data.data);
                setCitiesLoading(false);
            } catch (error) {
            } finally {
                setCitiesLoading(false);
            }
        };
        getCities();
    }, [direction]);

    useEffect(() => {
        reset({
            name: getTranslations(details?.translations?.name),
            description: getTranslations(details?.translations?.description),
            servicesProvided: getTranslations(details?.translations?.servicesProvided),
            content: getTranslations(details?.translations?.content),
            hotLine: details?.hot_line,
            status: details?.status,
            address: details?.address,
            mobile: details?.mobile,
            nameManager: details?.name_manager,
            phoneManager: details?.phone_manager,
            phone: details?.phone,
            serial_number: details?.serial_number,
            telegram: details?.telegram,
            whatsApp: details?.whats_app,
            facebook: details?.facebook,
            instagram: details?.instagram,
            establishmentDate: details?.establishment_date,
            webSiteLink: details?.web_site_link,
            imageId: details?.image?.id,
            coverId: details?.cover?.id,
            identificationDocId: details?.identification_doc?.id,
            numberOfBeneficiaries: details?.number_of_beneficiaries || 0,
            categoryCharityId: details?.categoryCharity.id,
            badgeId: details?.badge?.id,
            cityId: details?.city?.id,
        } as IUpdateCharity);
    }, [details]);

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const onSubmit = async (data: any) => {
        console.log(data);
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateCharity(details?.id, data)
                    : await actions.createCharity(data);
                navigate(-1);
            },
            fallback: (error) => {
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <span className={styles.title}>
                        {details ? t("update_charity") : t("add_charity")}
                    </span>
                    <Divider className={styles.divider} />
                    {loading.includes("details") ? (
                        <div style={{ height: "60vh" }}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                {languages.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`name[${lang.code}]`}
                                            label={`${t("name")} (${lang?.name})`}
                                            input={{ type: "text" }}
                                            rules={{ required: true }}
                                        />
                                    </Col>
                                ))}

                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`description[${lang.code}]`}
                                            label={`${t("description")} (${lang?.name})`}
                                            input={{ type: "text-area" }}
                                            rules={{ required: false }}
                                        />
                                    </Col>
                                ))}

                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`servicesProvided[${lang.code}]`}
                                            label={`${t("servicesProvided")} (${lang?.name})`}
                                            input={{ type: "text-area" }}
                                            rules={{ required: false }}
                                        />
                                    </Col>
                                ))}

                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`content[${lang.code}]`}
                                            label={`${t("content")} (${lang?.name})`}
                                            input={{ type: "text-area" }}
                                            rules={{ required: false }}
                                        />
                                    </Col>
                                ))}

                                <Col span={12}>
                                    <FieldBuilder
                                        name="hotLine"
                                        label={t("hotLine")}
                                        input={{ type: "text" }}
                                        rules={{
                                            pattern: {
                                                value: /^\+?[0-9]{8,15}$/,
                                                message: t("hotline_must_be_8_to_15_digits")
                                            }
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="address"
                                        label={t("address")}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="mobile"
                                        label={t("mobile")}
                                        input={{ type: "text" }}
                                        rules={{
                                            pattern: {
                                                value: /^\+?[0-9]{8,15}$/,
                                                message: t("mobile_must_be_8_to_15_digits_with_optional_plus")
                                            }
                                        }}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="nameManager"
                                        label={t("nameManager")}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="phoneManager"
                                        label={t("phoneManager")}
                                        input={{ type: "text" }}
                                        rules={{
                                            pattern: {
                                                value: /^\+?[0-9]{8,15}$/,
                                                message: t("mobile_must_be_8_to_15_digits_with_optional_plus")
                                            }
                                        }}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="telegram"
                                        label={t("telegram")}
                                        input={{ type: "text" }}
                                        rules={{
                                            pattern: {
                                                value: /^(@[a-zA-Z0-9_]{5,32}|(https?:\/\/)?(www\.)?(telegram\.me|t\.me)\/[a-zA-Z0-9_\-./?=#&]{1,200})$/i,
                                                message: t("invalid_telegram_url_or_username")
                                            }
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="whatsApp"
                                        label={t("whatsApp")}
                                        input={{ type: "text" }}
                                        rules={{
                                            pattern: {
                                                value: /^\+?[0-9]{8,15}$/,
                                                message: t("mobile_must_be_8_to_15_digits_with_optional_plus")
                                            }
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="facebook"
                                        label={t("facebook")}
                                        input={{ type: "text" }}
                                        rules={{
                                            pattern: {
                                                value: /^(https?:\/\/)?(www\.)?facebook\.com\/[a-zA-Z0-9_\-./?=#&]{1,200}$/i,
                                                message: t("invalid_facebook_url")
                                            }
                                        }}

                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="instagram"
                                        label={t("instagram")}
                                        input={{ type: "text" }}
                                        rules={{
                                            pattern: {
                                                value: /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9_\-./?=#&]{1,200}$/i,
                                                message: t("invalid_instagram_url")
                                            }
                                        }}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="establishmentDate"
                                        label={t("establishmentDate")}
                                        input={{ type: "date-picker" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="webSiteLink"
                                        label={t("webSiteLink")}
                                        input={{ type: "text" }}
                                        rules={{
                                            pattern: {
                                                value: /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:[0-9]+)?(\/.*)?$/i,
                                                message: t("invalid_url")
                                            }
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="numberOfBeneficiaries"
                                        label={t("numberOfBeneficiaries")}
                                        input={{ type: "number" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="badgeId"
                                        label={`${t("badge")}`}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            loading: tagsLoading,
                                            allowSearch: true,
                                            options: tags?.map((tag, index) => {
                                                return {
                                                    key: index,
                                                    label: tag.name,
                                                    value: tag.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="cityId"
                                        label={`${t("city")}`}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            loading: citiesLoading,
                                            allowSearch: true,
                                            options: cities?.map((city, index) => {
                                                return {
                                                    key: index,
                                                    label: city.name,
                                                    value: city.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="categoryCharityId"
                                        label={`${t("categories_charities")}`}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            loading: catsLoading,
                                            allowSearch: true,
                                            options: cats?.map((cat, index) => {
                                                return {
                                                    key: index,
                                                    label: cat.name,
                                                    value: cat.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FormItem label={t("status")}>
                                        <Controller
                                            name="status"
                                            control={control}
                                            rules={{ required: false }}
                                            render={({ field: { ...field } }) => {
                                                return (
                                                    <Select
                                                        loading={false}
                                                        // disabled={countryId ? false : true}
                                                        {...field}
                                                        style={{ width: "100%" }}
                                                        allowClear
                                                        placeholder={t("status")}
                                                    >
                                                        {[1, 2]?.map((tr, index) => {
                                                            return (
                                                                <Option key={index} value={tr}>
                                                                    {t(getUserStatus(tr))}
                                                                </Option>
                                                            );
                                                        })}
                                                    </Select>
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>

                                {/* Image Uploaders */}
                                <Col span={24}>
                                    <FormItem label={t("image")} required>
                                        <Controller
                                            name="imageId"
                                            rules={{ required: true }}
                                            render={({ field: { ref, ...field } }) => (
                                                <ImageUploader
                                                    {...field}
                                                    type="DRAGGER"
                                                    onChange={(response) => field.onChange(response?.id)}
                                                    imageFor={MediumFor.UserProfile}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                                {details && (
                                    <>
                                        <OldImage src={details?.image?.url} />
                                    </>
                                )}
                                <Col span={24}>
                                    <FormItem label={t("cover")}>
                                        <Controller
                                            name="coverId"
                                            render={({ field: { ref, ...field } }) => (
                                                <ImageUploader
                                                    {...field}
                                                    type="DRAGGER"
                                                    onChange={(response) => field.onChange(response?.id)}
                                                    imageFor={MediumFor.UserProfile}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                                {details && (
                                    <>
                                        <OldImage src={details?.cover?.url} />
                                    </>
                                )}
                                <Col span={24}>
                                    <FormItem label={t("identification_doc")}>
                                        <Controller
                                            name="identificationDocId"
                                            render={({ field: { ref, ...field } }) => (
                                                <ImageUploader
                                                    {...field}
                                                    type="DRAGGER"
                                                    onChange={(response) => field.onChange(response?.id)}
                                                    imageFor={MediumFor.UserProfile}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>

                                {details && (
                                    <>
                                        <OldImage src={details?.identification_doc?.url} />
                                    </>
                                )}
                            </Row>
                        </div>
                    )}
                </div>
                <div>
                    <div className={styles.formFooter}>
                        <Button onClick={() => navigate(-1)}>
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}

const CharityForm = () => {
    return (
        <CharityContextProvider>
            <Form />
        </CharityContextProvider>
    );
};
export default CharityForm;